import { useEffect, useRef, useState, type ReactNode } from 'react';
import { callApi } from '../lib/api.ts';
import { Icon } from './Icon.tsx';

type Status =
  | 'queued'
  | 'building'
  | 'ready'
  | 'failed'
  | 'no_project'
  | 'unconfigured'
  | 'unknown';

interface StatusResp {
  status: Status;
  url: string | null;
  stage?: string | null;
}

const LABEL: Record<Status, string> = {
  queued: 'Queued…',
  building: 'Building…',
  ready: 'Ready ✅',
  failed: 'Failed',
  no_project: 'No Pages project',
  unconfigured: 'Building ~1–2 min',
  unknown: '—',
};

// Info (blue) while in flight, green when ready, red on failure, warning for a missing project.
const COLOR: Record<Status, string> = {
  queued: 'bg-info-bright/10 text-info',
  building: 'bg-info-bright/10 text-info',
  unconfigured: 'bg-info-bright/10 text-info',
  ready: 'bg-success-bright/10 text-success',
  failed: 'bg-danger-bright/10 text-danger',
  no_project: 'bg-warning-bright/10 text-warning',
  unknown: 'bg-paper-2 text-ink-2',
};

const IN_PROGRESS: Status[] = ['queued', 'building', 'unconfigured'];
const TERMINAL: Status[] = ['ready', 'failed', 'no_project'];

const POLL_BUDGET_MS = 12 * 60 * 1000; // cover real CF build + queue times

function nextDelay(elapsedMs: number): number {
  if (elapsedMs < 60_000) return 4_000;
  if (elapsedMs < 180_000) return 8_000;
  return 15_000;
}

export function Spinner() {
  return (
    <svg className="h-3 w-3 animate-spin text-info" viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
  );
}

/**
 * Live Cloudflare deploy status for one build. The preview link only activates
 * once the deploy is ready, showing the EXACT CF URL.
 *
 * With `live`, it polls /api/build-status (backoff: 4s → 8s → 15s, ~12 min cap)
 * until terminal, re-checks on window focus, and calls onResolved when it first
 * reaches ready (so the parent can refetch). The poll self-terminates on a
 * terminal status regardless of `live`. The "check" button is the manual
 * fallback for non-live, unresolved builds.
 */
export function BuildStatus({
  buildId,
  initialUrl,
  live = false,
  onResolved,
  endpoint = 'build-status',
  readyLabel = (
    <>
      Ready <Icon name="check" title="ready" />
    </>
  ),
  openLabel = (
    <>
      open <Icon name="external" />
    </>
  ),
  pathSuffix = '',
  requestBody,
  chipOnly = false,
}: {
  buildId: string;
  initialUrl: string | null;
  live?: boolean;
  onResolved?: () => void;
  /** Status endpoint to poll: 'build-status' (preview) or 'promote-status'. */
  endpoint?: string;
  readyLabel?: ReactNode;
  openLabel?: ReactNode;
  /** Appended to the ready/derived URL's href (e.g. '/brand-kit.html' for a
   *  brand-preview build, whose page lives at a sub-path, not the deploy root). */
  pathSuffix?: string;
  /** POST body for the status endpoint (default { buildId }). E.g. site-status polls
   *  by { clientId } for the branch's latest preview (#GR23). buildId stays the identity. */
  requestBody?: Record<string, unknown>;
  /** #GR23: render ONLY the status pill — no open-link, derived-link or check button.
   *  Used for a per-section status chip where the single "Current site" link is the URL. */
  chipOnly?: boolean;
}) {
  const [resp, setResp] = useState<StatusResp | null>(
    initialUrl ? { status: 'ready', url: initialUrl } : null,
  );
  const [checking, setChecking] = useState(false);
  const fired = useRef(false);
  const terminalRef = useRef(initialUrl ? true : false);

  // Re-sync when a persisted preview_url arrives after mount (e.g. a table row
  // refetched once the build resolved) so the cell flips to Ready + open link.
  useEffect(() => {
    if (initialUrl) {
      setResp({ status: 'ready', url: initialUrl });
      terminalRef.current = true;
    }
  }, [initialUrl]);

  function apply(r: StatusResp) {
    setResp(r);
    if (TERMINAL.includes(r.status)) terminalRef.current = true;
    if (r.status === 'ready' && r.url && !fired.current) {
      fired.current = true;
      onResolved?.();
    }
  }

  async function fetchOnce(): Promise<StatusResp | null> {
    try {
      const r = await callApi<StatusResp>(endpoint, {
        method: 'POST',
        body: JSON.stringify(requestBody ?? { buildId }),
      });
      apply(r);
      return r;
    } catch {
      return null;
    }
  }

  async function check() {
    setChecking(true);
    try {
      const r = await fetchOnce();
      if (!r) setResp({ status: 'unknown', url: null });
    } finally {
      setChecking(false);
    }
  }

  useEffect(() => {
    if (!live || terminalRef.current) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const startedAt = Date.now();

    const loop = async () => {
      if (cancelled) return;
      const r = await fetchOnce();
      if (cancelled) return;
      const terminal = !!r && TERMINAL.includes(r.status);
      if (!terminal && Date.now() - startedAt < POLL_BUDGET_MS) {
        timer = setTimeout(loop, nextDelay(Date.now() - startedAt));
      }
    };
    void loop();

    // Returning to the tab re-checks immediately (without stacking the loop).
    const onFocus = () => {
      if (!cancelled && !terminalRef.current) void fetchOnce();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, buildId]);

  const status = resp?.status ?? 'unknown';
  const ready = status === 'ready' && !!resp?.url;
  const inProgress = IN_PROGRESS.includes(status);
  const href = (u: string) => `${u.replace(/\/$/, '')}${pathSuffix}`;

  return (
    <span className="inline-flex items-center gap-2">
      <span className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs ${COLOR[status]}`}>
        {inProgress && !checking && <Spinner />}
        {checking ? 'Checking…' : status === 'ready' ? readyLabel : LABEL[status]}
      </span>
      {!chipOnly && ready && (
        <a
          href={href(resp!.url!)}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-info underline"
        >
          {openLabel}
        </a>
      )}
      {!chipOnly && status === 'unconfigured' && resp?.url && (
        <a
          href={href(resp.url)}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-ink-2 underline"
          title="Derived URL — CF status not yet wired; may 404 until the deploy finishes"
        >
          (derived ↗)
        </a>
      )}
      {!chipOnly && !live && !ready && status !== 'unconfigured' && (
        <button
          onClick={check}
          disabled={checking}
          className="text-xs text-ink-2 underline hover:text-ink disabled:opacity-50"
        >
          check
        </button>
      )}
    </span>
  );
}
